import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import {
  getSliderById,
  handleCreateSlider,
  handleUpdateSlider,
} from "../../../API/Slider/sliderService";
import BreadCrumb from "../../breadcrumb";
import Header from "../../DashboardNavigation/header";
import Sidebar from "../../DashboardNavigation/sidebar";
import FileUpload from "../../FileUpload/FileUpload";
import { Button } from "../../ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "../../ui/form";
import { Heading } from "../../ui/heading";
import { Separator } from "../../ui/separator";
import {
  SliderFormValues,
  sliderSchema,
  UpdateSliderFormValues,
  updatesliderSchema,
} from "./slider-zod-schema";
import { LoadingOverlay } from "../../ui/loading-overlay";

export const SliderForm: React.ComponentType<any> = () => {
  const uploadUrl = process.env.REACT_APP_FILE_UPLOAD;

  const { id } = useParams();
  const initialData = id ? parseInt(id) : undefined;
  const router = useNavigate();
  const [loading, setLoading] = useState(false);
  const title = initialData ? "Modifier l'image" : "Ajouter une nouvelle image";
  const description = initialData
    ? "Modifier l'image"
    : "Ajouter une nouvelle image";
  const action = initialData ? "Mise à jour" : "Ajouter";

  const form = useForm<SliderFormValues | UpdateSliderFormValues>({
    resolver: zodResolver(initialData ? updatesliderSchema : sliderSchema),
    mode: "onChange",
  });

  const onSubmit = async () => {
    setLoading(true);
    const formData = form.getValues();
    try {
      if (initialData) {
        await handleUpdateSlider(initialData, formData, router);
      } else {
        await handleCreateSlider(formData, router);
      }
      setLoading(false);
    } catch (error) {
      setLoading(false);
    }
  };

  const breadcrumbItems = [
    { title: "Slider", link: "/admin-dashboard/sliders/" },
    { title: "Ajouter", link: "/admin-dashboard/slider/ajouter" },
  ];

  const [data, setData] = useState<any>();
  useEffect(() => {
    const fetchData = async () => {
      if (initialData) {
        const sliderData = await getSliderById(initialData);
        setData(sliderData);
        form.reset({
          image: sliderData.image
            ? [
                {
                  fileName: sliderData.image,
                  name: sliderData.image,
                  fileSize: 0,
                  size: 0,
                  key: sliderData.image,
                },
              ]
            : [],
        });
      }
    };

    if (initialData) {
      fetchData();
    }
  }, [initialData]);

  return (
    <>
      <Header />
      <div className="flex h-screen overflow-hidden">
        <Sidebar />
        <div className="w-full p-10">
          <BreadCrumb items={breadcrumbItems} />
          <div className="flex items-center justify-between">
            <Heading title={title} description={description} />
          </div>
          <Separator />

          <LoadingOverlay isVisible={loading} />
          <Form {...form}>
            <form
              className="w-full space-y-8"
              onSubmit={form.handleSubmit(onSubmit)}
            >
              {data?.image && (
                <div className="mt-4 mb-4">
                  <img
                    src={`${uploadUrl}/slider/${data.image}`}
                    alt="Old Image Preview"
                    className="w-10 h-10 h-auto"
                  />
                </div>
              )}
              <FormField
                control={form.control}
                name="image"
                render={() => (
                  <FormItem>
                    <FormControl>
                      <FileUpload
                        onChange={(files) => {
                          const mappedFiles = files.map((file) => ({
                            key: file.id,
                            fileName: file.fileName,
                            name: file.fileName,
                            fileSize: parseInt(file.fileSize),
                            size: parseInt(file.fileSize),
                            file: file.file,
                          }));
                          form.setValue("image", mappedFiles);
                        }}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <div className="flex space-x-5">
                <div>
                  <Button
                    disabled={loading}
                    isLoading={loading}
                    className="ml-auto"
                    type="submit"
                  >
                    {action}
                  </Button>
                </div>
                <div>
                  <Button
                    onClick={() => router(-1)}
                    disabled={loading}
                    isLoading={loading}
                    className="ml-auto bg-red-500"
                    type="button"
                  >
                    {"Annuler"}
                  </Button>
                </div>
              </div>
            </form>
          </Form>
        </div>
      </div>
    </>
  );
};
